import React from 'react'

const getDotNotatedObjectKeys = (object: Record<string, unknown>): string[] => {
  const keys = Object.keys(object)
  const dotNotatedKeys: (string | string[])[] = keys.map(
    (key): string | string[] => {
      if (typeof object[key] === 'object') {
        const subKeys: string[] = getDotNotatedObjectKeys(
          object[key] as Record<string, unknown>,
        )
        return subKeys.map((subKey: string) => `${key}.${subKey}`)
      }
      return key
    },
  )
  return dotNotatedKeys.flat()
}

const buildDotNotatedObjectByKeyString = (
  parentObject: Record<string, unknown>,
  keyString: string,
  value: string,
) => {
  const keySplits = keyString.split('.')
  let object = parentObject
  for (let i = 0; i < keySplits.length; i++) {
    const key = keySplits[i]
    // Prevent prototype pollution
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue
    }
    if (i < keySplits.length - 1) {
      if (!object[key]) {
        object[key] = {}
      }
      object = object[key] as Record<string, unknown>
      continue
    }

    object[key] = value
  }
  return object
}

export const getDotWalkedObjectMap = (
  object: Record<string, unknown>,
  depthCutoff = 0,
): Record<string, unknown> => {
  const dotNotatedKeys = getDotNotatedObjectKeys(object)
  const depthCutoffKeyHashSet = new Set<string>()
  dotNotatedKeys.map((key: string) => {
    const keySplits = key.split('.')
    let rebuiltKey = keySplits[0]
    for (let i = 1; i < keySplits.length - depthCutoff; i++) {
      rebuiltKey = `${rebuiltKey}.${keySplits[i]}`
    }
    depthCutoffKeyHashSet.add(rebuiltKey)
  })
  const parentObject: Record<string, unknown> = {}
  depthCutoffKeyHashSet.forEach((key) => {
    buildDotNotatedObjectByKeyString(parentObject, key, key)
  })

  return parentObject
}

export const getShallowObjectKeyMap = (
  object: Record<string, unknown>,
): Record<string, string> => {
  const keys = Object.keys(object)
  const keyMap: Record<string, string> = {}
  keys.forEach((key) => {
    keyMap[key] = key
  })
  return keyMap
}

export const getName = <T extends PropertyKey>(object: SingleKeyObject<T>) => {
  return Object.keys(object)[0]
}

export const areValuesEqual = <T>(a: T, b: T): boolean => {
  // Handle primitive types and null/undefined
  if (a === b) return true
  if (a === null || b === null) return false
  if (a === undefined || b === undefined) return false

  // Handle different types
  if (typeof a !== typeof b) return false

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => areValuesEqual(item, b[index]))
  }

  // Handle Maps
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false
    for (const [key, value] of a) {
      if (!b.has(key)) return false
      if (!areValuesEqual(value, b.get(key))) return false
    }
    return true
  }

  // Handle Sets
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false
    for (const item of a) {
      if (!b.has(item)) return false
    }
    return true
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)

    if (keysA.length !== keysB.length) return false

    return keysA.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(b, key) &&
        areValuesEqual(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key],
        ),
    )
  }

  return false
}
/**
 * Get the text content of a node.
 * @param node - The node to get the text content of.
 * @returns The text content of the node.
 */
export const getNodeText = (
  node?: string | React.ReactNode | number,
): string => {
  if (!node) return ''
  if (node instanceof Array) return node.map(getNodeText).join(' ')
  if (React.isValidElement(node)) {
    const props = node.props as { children?: React.ReactNode }
    return getNodeText(props.children)
  }
  return String(node)
}
